import React from 'react';
import { AvailabilityOption, AVAILABILITY_TYPES, AVAILABILITY_LABELS, AVAILABILITY_DEFAULT_DAYS, createEmptyAvailabilityOption } from '@/domain/entities';

interface AvailabilityOptionsEditorProps {
  options: AvailabilityOption[];
  onChange: (options: AvailabilityOption[]) => void;
  disabled?: boolean;
}

export const AvailabilityOptionsEditor: React.FC<AvailabilityOptionsEditorProps> = ({
  options,
  onChange,
  disabled = false,
}) => {
  // Убедимся, что все три типа присутствуют
  const ensureAllTypes = (opts: AvailabilityOption[]): AvailabilityOption[] => {
    const types: Array<'in_stock' | 'warehouse' | 'abroad'> = [
      AVAILABILITY_TYPES.IN_STOCK,
      AVAILABILITY_TYPES.WAREHOUSE,
      AVAILABILITY_TYPES.ABROAD,
    ];
    
    const result: AvailabilityOption[] = [];
    
    types.forEach(type => {
      const existing = opts.find(o => o.type === type);
      if (existing) {
        result.push(existing);
      } else {
        result.push(createEmptyAvailabilityOption(type));
      }
    });
    
    return result;
  };

  const currentOptions = ensureAllTypes(options);

  const handleOptionChange = (index: number, field: keyof AvailabilityOption, value: any) => {
    const updated = [...currentOptions];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // Автоматически пересчитываем availableQuantity
    if (field === 'quantity' || field === 'reservedQuantity') {
      updated[index].availableQuantity = updated[index].quantity - updated[index].reservedQuantity;
    }
    
    onChange(updated);
  };

  const handleToggleEnabled = (index: number) => {
    const updated = [...currentOptions];
    updated[index].enabled = !updated[index].enabled;
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      {currentOptions.map((option, index) => (
        <div
          key={option.type}
          className={`rounded-lg border ${
            option.enabled
              ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/30'
              : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
          } p-3 transition-colors`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${
                option.enabled ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
              }`}>
                {option.label}
              </span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={option.enabled}
                onChange={() => handleToggleEnabled(index)}
                disabled={disabled}
                className="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 bg-white dark:bg-gray-700"
              />
              <span className="text-xs text-gray-600 dark:text-gray-400">Активно</span>
            </label>
          </div>

          {option.enabled && (
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Кол-во</label>
                <input
                  type="number"
                  min="0"
                  value={option.quantity}
                  onChange={(e) => handleOptionChange(index, 'quantity', parseInt(e.target.value) || 0)}
                  disabled={disabled}
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Цена (₽)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={option.price}
                  onChange={(e) => handleOptionChange(index, 'price', parseFloat(e.target.value) || 0)}
                  disabled={disabled}
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Скидка (%)</label>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={option.discount || ''}
                  onChange={(e) => handleOptionChange(index, 'discount', e.target.value ? parseInt(e.target.value) : null)}
                  disabled={disabled}
                  placeholder="-"
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Дней</label>
                <input
                  type="number"
                  min="0"
                  value={option.deliveryDays}
                  onChange={(e) => handleOptionChange(index, 'deliveryDays', parseInt(e.target.value) || 0)}
                  disabled={disabled}
                  className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800"
                />
              </div>
            </div>
          )}
        </div>
      ))}

      <div className="text-xs text-gray-500 dark:text-gray-400 pt-1">
        💡 Активировано: {currentOptions.filter(o => o.enabled).length} из {currentOptions.length}
      </div>
    </div>
  );
};

